import axios from 'axios';
import { apiErrorMessage } from '../edit/editApi';
import { getCurrentMonthStr } from '../edit/editCalc';
import { SummaryField, SummaryRow } from '../summary/summaryApi';

/** Базовый адрес «Дир. отчета» (все запросы проверяются сервером по сессии и правам роли) */
export const DIRECTOR_API = '/api/v2/reports/director';

/** ok - есть данные за месяц; empty - записей за месяц нет; no_table - у аптеки нет таблицы отчетов */
export type DirectorStatus = 'ok' | 'empty' | 'no_table';

export interface DirectorPharmacy {
  username: string;
  pharmacyNum: string;
  fullName: string | null;
  status: DirectorStatus;
  rows: SummaryRow[];
  totals: Record<string, number>;
}

export interface DirectorResponse {
  success: boolean;
  month: string;
  fields: SummaryField[];
  /** Аптек по текущему поиску (не только на этой странице) */
  total: number;
  page: number;
  pages: number;
  pageSize: number;
  pharmacies: DirectorPharmacy[];
}

export interface DirectorExportResponse {
  success: boolean;
  month: string;
  fields: SummaryField[];
  /** Только аптеки, по которым за месяц есть данные */
  pharmacies: DirectorPharmacy[];
}

export interface DirectorQuery {
  month: string;
  page: number;
  search: string;
}

/** Сколько последних месяцев можно выбрать в списке */
const MONTHS_IN_LIST = 12;

/** Последние месяцы от текущего в виде YYYY-MM, самый свежий первым */
export function recentMonths(): string[] {
  const [y, m] = getCurrentMonthStr().split('-').map(Number);
  const list: string[] = [];
  for (let i = 0; i < MONTHS_IN_LIST; i += 1) {
    list.push(new Date(Date.UTC(y, m - 1 - i, 1)).toISOString().slice(0, 7));
  }
  return list;
}

/** Страница аптек за месяц (silent - перезагрузка без новой записи в журнал) */
export async function fetchDirector(query: DirectorQuery, silent: boolean): Promise<DirectorResponse> {
  const res = await axios.get(DIRECTOR_API, {
    params: {
      month: query.month,
      page: query.page,
      search: query.search || undefined,
      silent: silent ? 1 : undefined
    }
  });
  if (!res.data || !res.data.success) {
    throw new Error((res.data && res.data.message) || 'Не удалось загрузить отчет');
  }
  return res.data as DirectorResponse;
}

/** Данные всех подчиненных аптек для общего файла Excel (выдача данных фиксируется в журнале сервером) */
export async function fetchDirectorExport(month: string): Promise<DirectorExportResponse> {
  const res = await axios.get(`${DIRECTOR_API}/export`, { params: { month } });
  if (!res.data || !res.data.success) {
    throw new Error((res.data && res.data.message) || 'Не удалось получить данные для выгрузки');
  }
  return res.data as DirectorExportResponse;
}

/** Запись в журнал о выгрузке одной аптеки (файл собирается в браузере); сбой журнала не мешает работе */
export async function auditDirector(username: string, month: string): Promise<void> {
  try {
    await axios.post(`${DIRECTOR_API}/audit`, { username, month });
  } catch (err) {
    console.error('Не удалось записать выгрузку в журнал:', apiErrorMessage(err, ''));
  }
}

// ---------------------------------------------------------------------------
// Запомненный выбор (только фильтры: данные всегда запрашиваются заново у сервера)
// ---------------------------------------------------------------------------

const FILTERS_KEY = 'directorReportFilters';

export interface SavedFilters {
  month: string;
  search: string;
}

export function loadFilters(): SavedFilters | null {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (v && typeof v.month === 'string' && typeof v.search === 'string') return v as SavedFilters;
  } catch (e) {
    // хранилище недоступно или повреждено - начинаем с чистого выбора
  }
  return null;
}

export function saveFilters(filters: SavedFilters): void {
  try {
    sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  } catch (e) {
    // не критично: выбор просто не запомнится
  }
}
